import { Media } from "src/types/media";

const hasBlobConstructor =
  typeof Blob !== "undefined" &&
  (() => {
    try {
      return Boolean(new Blob());
    } catch {
      return false;
    }
  })();

const hasArrayBufferViewSupport =
  hasBlobConstructor &&
  typeof Uint8Array !== "undefined" &&
  (() => {
    try {
      return new Blob([new Uint8Array(100)]).size === 100;
    } catch {
      return false;
    }
  })();

const hasToBlobSupport =
  typeof HTMLCanvasElement !== "undefined"
    ? HTMLCanvasElement.prototype.toBlob
    : false;

const hasBlobSupport =
  hasToBlobSupport ||
  (typeof Uint8Array !== "undefined" &&
    typeof ArrayBuffer !== "undefined" &&
    typeof atob !== "undefined");

const hasReaderSupport =
  typeof FileReader !== "undefined" || typeof URL !== "undefined";

export function resizeImage(
  file: File,
  maxDimensions: { width: number; height: number }
): Promise<Blob | File> {
  return new Promise((resolve, reject) => {
    if (!isSupported() || !file.type.match(/image.*/)) {
      resolve(file);
      return;
    }

    if (file.type.match(/image\/gif/)) {
      resolve(file);
      return;
    }

    const image = document.createElement("img");

    image.onload = () => {
      let width = image.width;
      let height = image.height;
      let isTooLarge = false;

      if (width >= height && width > maxDimensions.width) {
        height *= maxDimensions.width / width;
        width = maxDimensions.width;
        isTooLarge = true;
      } else if (height > maxDimensions.height) {
        width *= maxDimensions.height / height;
        height = maxDimensions.height;
        isTooLarge = true;
      }

      if (!isTooLarge) {
        resolve(file);
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(image, 0, 0, width, height);

      if (hasToBlobSupport) {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Canvas conversion failed"));
              return;
            }
            resolve(blob);
          },
          file.type,
          file.type === "image/jpeg" ? 0.9 : undefined
        );
      } else {
        const blob = toBlob(canvas, file.type);
        resolve(blob);
      }
    };

    image.onerror = () => reject(new Error("Invalid image file"));

    loadImage(image, file);
  });
}

function toBlob(canvas: HTMLCanvasElement, type: string): Blob {
  const dataURI = canvas.toDataURL(type);
  const dataURIParts = dataURI.split(",");
  const byteString =
    dataURIParts[0].indexOf("base64") >= 0
      ? atob(dataURIParts[1])
      : decodeURIComponent(dataURIParts[1]);

  const arrayBuffer = new ArrayBuffer(byteString.length);
  const intArray = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    intArray[i] = byteString.charCodeAt(i);
  }

  const mimeString = dataURIParts[0].split(":")[1].split(";")[0];

  if (hasBlobConstructor) {
    return new Blob([hasArrayBufferViewSupport ? intArray : arrayBuffer], {
      type: mimeString,
    });
  } else {
    // legacy
    // @ts-ignore
    const bb = new BlobBuilder();
    bb.append(arrayBuffer);
    return bb.getBlob(mimeString);
  }
}

function loadImage(
  image: HTMLImageElement,
  file: File,
  callback?: () => void
) {
  if (typeof URL === "undefined") {
    const reader = new FileReader();
    reader.onload = (evt) => {
      image.src = evt.target?.result as string;
      if (callback) callback();
    };
    reader.readAsDataURL(file);
  } else {
    image.src = URL.createObjectURL(file);
    if (callback) callback();
  }
}

function isSupported() {
  return (
    typeof HTMLCanvasElement !== "undefined" &&
    hasBlobSupport &&
    hasReaderSupport
  );
}

export function getLogoUrl(logo?: string | Media | null): string | undefined {
  if (!logo) return undefined;
  if (typeof logo === "string") return logo;
  return logo.thumbnail;
}
